/**
 * Cyber theme — fragment shader.
 *
 * Adapted from the shadertoy "Harmonic Pixels" source. Three
 * channel passes of 50 oscillating orbs each (150 inner iterations
 * per pixel) layered through a CRT-style scanline-modulated
 * background — synthwave / cyberpunk character.
 *
 * Adaptations vs the shadertoy source (verbatim orb() helper,
 * mutated bg + main):
 *
 *   1. `mainImage(out vec4 fragColor, in vec2 fragCoord)` → `void
 *      main()` reading `gl_FragCoord.xy` directly. Required for
 *      `#version 300 es` (WebGL2 fragment-shader entry point
 *      writes to a declared `out vec4 fragColor` global, not a
 *      mainImage parameter). Header switched from
 *      `#ifdef GL_ES precision mediump float; #endif` to
 *      `precision highp float;` because the 150-iteration per-
 *      pixel work accumulates float math that mediump can't hold
 *      cleanly on mobile GPUs.
 *
 *   2. `bgColor()` literals replaced with `uniform vec3 uBgColorA`
 *      + `uniform vec3 uBgColorB`. Return type changed to vec3
 *      (alpha is always 1.0 in the original; carrying it adds
 *      nothing). Unused `pos` parameter dropped. The scanline
 *      modulation (`* f` where f = `mod(fragCoord.y, 3.0)`) stays
 *      verbatim — that's the CRT character of the shader.
 *
 *   3. The `vec4 color = vec4(r, q*g, p*b, 1.0)` assembly is
 *      re-channelled through `uniform vec3 uChannelColor1/2/3`.
 *      The `p`/`q` channel-modulation pattern (animation
 *      breathing) is preserved — channels 2 and 3 still
 *      cross-modulate via `q` and `p` for the original animation
 *      feel; channel 1 stays at full intensity.
 *
 *   4. `uKwhScale` (0-1) drives the orb-oscillation amplitude:
 *      `const float amp = 0.3` becomes `float amp = 0.01 +
 *      uKwhScale * 0.45` (range widened post-runtime-review — amp
 *      is a motion-range knob, not a brightness knob, so the prior
 *      0.08→0.40 range read as a snap in the picker). At session
 *      start (uKwhScale=0) amp is 0.01 — orbs almost still. At
 *      session complete (uKwhScale=1) orbs swing dramatically at
 *      0.46 amplitude (above the original 0.3 constant for end-of-
 *      session payoff). Picker preview lands at uKwhScale=0.8 →
 *      amp 0.37, clearly visible motion against the 14.4s wind-up.
 *
 * Cache key: 'cyber:dark' or 'cyber:light'. Source is identical
 * post-CYBER_VARIANT injection today; the variant suffix
 * distinguishes compiled programs for forward-compat (future
 * variant-specific branching can gate on `#if CYBER_VARIANT ==
 * ...` without dispatcher changes).
 *
 * Performance note: 50 iterations × 3 channels = 150 inner-loop
 * sin/cos/div ops per pixel. Heavier than Aurora's six-octave
 * simplex noise. Not hardware-conditional-gated yet (per build-
 * prompt directive — observe runtime first). If mobile jank
 * surfaces post-merge, the lever is `N_ITERATIONS` (currently
 * 50 — dropping to 32 cuts work by ~36% with minimal visual
 * impact on the wave aggregation since the orbs blend).
 *
 * `uIntensity` (powerKw-driven) intentionally not declared — the
 * shader doesn't need it. Dispatcher's per-frame bind passes it
 * for every shader; `bindUniforms` silently skips uniforms whose
 * getUniformLocation returns null. Future hookup (e.g. brightening
 * orbs with instantaneous power) can declare it then.
 */
export const CYBER_SHADER_SOURCE = `#version 300 es
precision highp float;
#define CYBER_DARK  0
#define CYBER_LIGHT 1
// CYBER_VARIANT injected via prepend:
// #define CYBER_VARIANT CYBER_DARK
uniform float iTime;
uniform vec2 iResolution;
uniform float uKwhScale;
// Tap-to-chaos interaction (Cyber). 0 at rest (no jitter), so the orbs sit in
// their ordered baseline; a tap scrambles them, then they snap back fast.
uniform float uChaos;
uniform vec3 uChannelColor1;
uniform vec3 uChannelColor2;
uniform vec3 uChannelColor3;
uniform vec3 uBgColorA;
uniform vec3 uBgColorB;
out vec4 fragColor;

const float PI = 3.141592654;
const float N_ITERATIONS = 50.0;

float usin(float t) {
  return sin(t) * 0.5 + 0.5;
}

float orb(float x0, float freqMul, float amp, float aspect, vec3 pos, float glow, float ampFreq) {
  float c = 0.0;
  float s = sin(iTime*ampFreq);
  for (float i = 0.0; i < N_ITERATIONS; i++) {
    float freq = freqMul*(i+1.0)/N_ITERATIONS;
    float x = x0+i/N_ITERATIONS;
    float y = 0.5 + s*sin(freq*iTime)*amp;

    vec3 circPos = vec3(x*aspect, y, 1.0);
    float f = 0.17;

    float sinTime = 1.0-usin(iTime)*0.3;
    float dx = pos.x - circPos.x;
    float dy = pos.y - circPos.y;
    float d = glow*(dx*dx + dy*dy);
    c += f/(d*50.*sinTime);
  }

  return c;
}

// Bg color — CRT scanline modulation gates per-row brightness.
// Per-variant palette injected via uBgColorA / uBgColorB
// uniforms (dark: dark-navy → slightly-purple-navy; light:
// lavender-cream → deeper-lavender-cream). The * f scanline
// multiplier stays from the shadertoy source — that is the
// raster-tube character of the shader, removing it would lose
// the cyberpunk feel.
vec3 bgColor(vec2 fragCoord) {
  float f = mod(fragCoord.y / 1.0, 3.0);
  float t = usin(iTime);
  vec3 bgBase = mix(uBgColorA, uBgColorB, t);
  return bgBase * f;
}

void main() {
  vec2 fragCoord = gl_FragCoord.xy;
  float aspect = iResolution.x / iResolution.y;
  vec3 pos = vec3(fragCoord.xy / iResolution.xy, 1.0);
  pos.x *= aspect;

  // Tap-to-chaos: jitter the sample by a fast high-frequency noise scaled by
  // the impulse, so a tap scrambles the ordered orbs into glitchy chaos; uChaos
  // is 0 at rest (no jitter), and its fast decay snaps the pattern back.
  vec2 chaosN = vec2(
    sin(fragCoord.x * 12.9 + fragCoord.y * 4.7 + iTime * 50.0),
    cos(fragCoord.y * 9.3 - fragCoord.x * 3.1 + iTime * 43.0)
  );
  pos.xy += chaosN * uChaos * 0.2;

  // uKwhScale-driven amplitude (range widened post-runtime-review).
  // amp controls the orbs' vertical oscillation swing, NOT their
  // brightness — so the old 0.08→0.40 range read as a snap in the
  // picker (orbs equally bright throughout, only the motion range
  // grew, which the eye misses at preview size). Widened so the
  // start state is near-dormant: at session start (kwh=0) amp is
  // 0.01 — orbs almost still, stacked flat. At the 80% picker
  // preview amp is 0.37 — dramatic swing. At session complete
  // (kwh=1) amp is 0.46 — maximum oscillation (above the original
  // 0.3 constant for end-of-session payoff).
  float amp = 0.01 + uKwhScale * 0.45;
  const float x0 = 0.5/N_ITERATIONS;

  // Three channel passes — each computes the aggregated orb
  // intensity for its frequency band. Channel 1 sweeps slow
  // (freqMul 0.3), channel 2 mid (0.6), channel 3 fast (0.9);
  // glow falloff and ampFreq stagger to give each channel its
  // own visible rhythm.
  float intensity1 = orb(x0, 0.3, amp, aspect, pos, 2.5, 0.5);
  float intensity2 = orb(x0, 0.6, amp, aspect, pos, 4.5, 0.75);
  float intensity3 = orb(x0, 0.9, amp, aspect, pos, 6.5, 0.5);

  // p / q channel cross-modulation — preserved from the
  // shadertoy source. As iTime advances p ranges 0-1 and q
  // ranges 1.5-0.5; channels 2 and 3 wax + wane in
  // counterpoint, channel 1 stays at full intensity. Gives the
  // overall image a slow breathing character on top of the
  // per-channel oscillations.
  float p = usin(iTime*1.2)*0.5+0.5;
  float q = 1.5 - p;

  vec3 orbColor =
      uChannelColor1 * intensity1
    + uChannelColor2 * (q * intensity2)
    + uChannelColor3 * (p * intensity3);

  // Multiplicative bg blend — the bg's scanline pattern gates
  // the orb light per-pixel-row, producing the CRT character.
  // Then a final 10%-amplitude breathing modulation across
  // 2*iTime caps the composition.
  vec3 bg = bgColor(fragCoord);
  vec3 finalColor = orbColor * bg;
  finalColor *= 1.0 - usin(2.0*iTime)*0.1;

  // Canonical two-term wind-up, applied once at the composition
  // point so every layer (orbs, scanline bg, breathing modulation)
  // gets the same treatment. Composed in this order: desaturate
  // first, then darken.
  //
  //   1. kwhSaturation (grayscale -> full colour). Linear in
  //      uKwhScale. Mix between the Rec.709 luminance of the
  //      output and the full-colour output, so the start state is
  //      colourless form and the end state is full hue. Rec.709
  //      weights (0.2126/0.7152/0.0722) are the sRGB standard the
  //      shaders operate in (not Rec.601). Linear curve for now;
  //      tune to pow()/smoothstep() in a follow-up only if the
  //      live walk wants it.
  //   2. kwhBrightness (near-black -> full). Unchanged: 0.05 at
  //      session start, 0.81 at the 80% picker preview, 1.0 at
  //      session complete. Multiplies AFTER desaturation so it
  //      darkens whichever (gray or colour) content step 1
  //      produced.
  //
  // Net: colourless + dim at session start, vibrant + bright at
  // full charge — the wind-up reads as "coming to life."
  float luminance = dot(finalColor, vec3(0.2126, 0.7152, 0.0722));
  float kwhSaturation = uKwhScale;
  vec3 windupColor = mix(vec3(luminance), finalColor, kwhSaturation);
  float kwhBrightness = 0.05 + uKwhScale * 0.95;
  fragColor = vec4(windupColor * kwhBrightness, 1.0);
}`;

/**
 * Cyber shader variant names. Two values matching the
 * `Theme.shaderVariant` axis on the Cyber family registry entry
 * and the per-variant uniform tuples in `CYBER_UNIFORMS` below.
 */
export type CyberVariant = 'cyber-dark' | 'cyber-light';

/**
 * Numeric values for the `#define CYBER_VARIANT` injection.
 * Match the literal `#define CYBER_DARK 0 / CYBER_LIGHT 1`
 * declarations at the top of the GLSL string. Currently no
 * `#if CYBER_VARIANT == ...` branches consume the value
 * (palette is uniform-injected, not source-branched), but the
 * macro is declared + injected for forward-compat with future
 * per-variant motion or iteration-count tweaks.
 */
export const CYBER_VARIANT_CONSTANTS: Record<CyberVariant, number> = {
  'cyber-dark': 0,
  'cyber-light': 1,
};

/**
 * Per-variant uniform tuples. Tuples are arrays so they can be
 * passed to `bindUniforms` directly (which dispatches on tuple
 * length). All vec3 values in linear-RGB [0, 1] floats.
 *
 * Dark variant: hot magenta + electric purple + deep indigo
 * channels on dark navy → slightly-purple navy scanline bg.
 * Indigo (channel 3) intentionally darker than Neon's pink to
 * avoid family-identity overlap on the picker grid.
 *
 * Light variant: pastel magenta + pastel lavender + pastel
 * sky-violet channels on lavender-cream scanline bg. Softer
 * composition matches the "cyber but daytime" reading; pure
 * white bg would wash out the scanline modulation.
 */
export const CYBER_UNIFORMS = {
  'cyber-dark': {
    uChannelColor1: [0.95, 0.20, 0.85],
    uChannelColor2: [0.55, 0.30, 0.95],
    uChannelColor3: [0.30, 0.45, 0.95],
    uBgColorA: [0.04, 0.06, 0.14],
    uBgColorB: [0.10, 0.04, 0.18],
  },
  'cyber-light': {
    uChannelColor1: [0.85, 0.50, 0.85],
    uChannelColor2: [0.65, 0.55, 0.90],
    uChannelColor3: [0.55, 0.65, 0.90],
    uBgColorA: [0.96, 0.94, 0.98],
    uBgColorB: [0.92, 0.88, 0.96],
  },
} as const satisfies Record<CyberVariant, {
  uChannelColor1: readonly [number, number, number];
  uChannelColor2: readonly [number, number, number];
  uChannelColor3: readonly [number, number, number];
  uBgColorA: readonly [number, number, number];
  uBgColorB: readonly [number, number, number];
}>;

/**
 * Returns the Cyber shader source with `#define CYBER_VARIANT
 * <number>` injected immediately after the `#version 300 es`
 * directive. GLSL requires `#version` to be the first directive
 * in the source; the inject lands at line 2 so it's in scope for
 * any future `#if CYBER_VARIANT == ...` branches in the body.
 *
 * Called by the WebGL compile pipeline in
 * ShaderChargingAnimation.tsx to produce one compiled program
 * per cyber variant. Each variant compiles to its own GL program
 * object even though the source post-injection is currently
 * identical between dark and light (the CYBER_VARIANT macro is
 * unused in the body today; declared for forward-compat). The
 * cache key — `'cyber:dark'` / `'cyber:light'` — keeps the two
 * programs distinct in the per-context cache.
 *
 * Mirrors the prependAuroraVariant + prependOrbKind shapes.
 */
export function prependCyberVariant(variant: CyberVariant): string {
  const kind = CYBER_VARIANT_CONSTANTS[variant];
  const lines = CYBER_SHADER_SOURCE.split('\n');
  const versionIdx = lines.findIndex((line) => line.startsWith('#version'));
  if (versionIdx === -1) {
    // Defensive — should never trip since the source above
    // starts with `#version 300 es`. Returning the raw source
    // would produce a compile error downstream (no CYBER_VARIANT
    // defined), which is the right failure surface.
    return CYBER_SHADER_SOURCE;
  }
  const before = lines.slice(0, versionIdx + 1);
  const after = lines.slice(versionIdx + 1);
  const inject = `#define CYBER_VARIANT ${kind}`;
  return [...before, inject, ...after].join('\n');
}
